import { Fragment, useEffect, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';

import { Link, routes } from '@redwoodjs/router';
import { MetaTags, useQuery } from '@redwoodjs/web';
import { toast } from '@redwoodjs/web/toast';

import Loader from 'src/components/Loader/Loader';
import MainCard from 'src/components/MainCard/MainCard';
import { GET_PRODUCT } from 'src/graphql/queries/product.query';
import { ProductStatusChip } from 'src/utils/productUtils';
import { formatProductId } from 'src/utils/utils';

type ViewProductProps = {
    id: number;
};

const ProductPage = ({ id }: ViewProductProps) => {
    const columns: GridColDef[] = [
        { field: 'id', headerName: 'Product ID', width: 150 },
        { field: 'name', headerName: 'Product Name', width: 350 },
        { field: 'type', headerName: 'Product Type', width: 350 },
    ];

    const { loading, error, data } = useQuery(GET_PRODUCT, { variables: { id } });
    const [imageUrl, setImageUrl] = useState<string | null>(null);

    useEffect(() => {
        if (data?.product?.imageUrl) {
            setImageUrl(data.product.imageUrl);
        }
    }, [data?.product?.imageUrl]);

    if (loading) {
        return <Loader />;
    }

    if (error) return <>{toast.error(`Error! ${error.message}`)}</>;

    if (!data || !data.product) {
        return <Loader />;
    }

    const { product } = data;

    const productId = product.type === 'ONLINE' ? product?.onlineId : product?.terminalId;

    return (
        <>
            <MetaTags title="Product" description="View Product" />
            <Stack spacing={5}>
                <MainCard boxShadow>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <h1>View Product Catalogue</h1>
                    </Box>
                </MainCard>
                <MainCard boxShadow>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'space-between',
                        }}
                    >
                        <h2 className="font-normal mb-0 mt-10">Product Details</h2>
                        <Link to={routes.editProduct({ id })}>
                            <Box sx={{ padding: '5px', borderRadius: '2px', color: '#000' }}>
                                <EditIcon />
                            </Box>
                        </Link>
                    </Box>

                    <Stack spacing={7}>
                        <Box className="mt-20">
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={4}>
                                        <Box>
                                            <Typography fontWeight={600}>Product Name</Typography>
                                            <Typography
                                                variant="h3"
                                                className="mt-10 font-normal black-200"
                                            >
                                                {product?.name}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography fontWeight={600}>
                                                Product Catalogue
                                            </Typography>
                                            <Typography
                                                variant="h3"
                                                className="mt-10 font-normal black-200"
                                            >
                                                {product?.catalogue}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography fontWeight={600}>Product Type</Typography>
                                            <Typography
                                                variant="h3"
                                                className="mt-10 font-normal black-200"
                                            >
                                                {product?.type}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography fontWeight={600}>Product Image</Typography>
                                            <img
                                                src={imageUrl}
                                                alt="product"
                                                width="120px"
                                                className="mt-10"
                                            />
                                        </Box>
                                    </Stack>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Stack spacing={4}>
                                        <Box>
                                            <Typography fontWeight={600}>Product ID</Typography>

                                            <Box className="mt-10">
                                                <Typography
                                                    variant="h3"
                                                    className="mt-10 font-normal black-200"
                                                >
                                                    {formatProductId(product?.type, productId)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Typography fontWeight={600}>Product Status</Typography>
                                            <Box className="mt-10">
                                                <ProductStatusChip status={product?.status} />
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Typography fontWeight={600}>
                                                Product Description
                                            </Typography>
                                            <Box className="mt-10 font-normal black-200">
                                                {product?.description &&
                                                    product?.description.map((desc, index) => {
                                                        return (
                                                            <Fragment key={`description_${index}`}>
                                                                {desc.type ===
                                                                'unordered-list-item' ? (
                                                                    <li className={`${desc.type}`}>
                                                                        {desc.text}
                                                                    </li>
                                                                ) : (
                                                                    <div>{desc.text}</div>
                                                                )}
                                                            </Fragment>
                                                        );
                                                    })}
                                            </Box>
                                        </Box>
                                        <Box>
                                            <Typography fontWeight={600}>
                                                Product Countries
                                            </Typography>
                                            <Box className="mt-10 font-normal black-200">
                                                {product?.countries &&
                                                    product?.countries.map((country, index) => {
                                                        return (
                                                            <Fragment key={`country_${index}`}>
                                                                {index === 0 ? '' : ', '}
                                                                {country.name}
                                                            </Fragment>
                                                        );
                                                    })}
                                            </Box>
                                        </Box>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </Box>

                        {product && product?.primaryProducts && (
                            <Box>
                                <Typography fontWeight={600} mb={2}>
                                    Primary Products
                                </Typography>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <DataGridPro
                                        rows={product?.primaryProducts}
                                        getRowId={(row) => {
                                            return row.id;
                                        }}
                                        columns={columns}
                                        autoPageSize
                                        disableColumnFilter
                                        disableColumnMenu
                                        disableRowSelectionOnClick
                                        hideFooterRowCount
                                        paginationModel={{
                                            pageSize: product?.primaryProducts.length || 0,
                                            page: 0,
                                        }}
                                        pageSizeOptions={[product?.primaryProducts.length || 0]}
                                    />
                                </div>
                            </Box>
                        )}

                        {product && product?.secondaryProducts && (
                            <Box>
                                <Typography fontWeight={600} mb={2}>
                                    Secondary Products
                                </Typography>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <DataGridPro
                                        rows={product?.secondaryProducts}
                                        getRowId={(row) => {
                                            return row.id;
                                        }}
                                        columns={columns}
                                        autoPageSize
                                        disableColumnFilter
                                        disableColumnMenu
                                        disableRowSelectionOnClick
                                        hideFooterRowCount
                                        paginationModel={{
                                            pageSize: product?.secondaryProducts.length || 0,
                                            page: 0,
                                        }}
                                        pageSizeOptions={[product?.secondaryProducts.length || 0]}
                                    />
                                </div>
                            </Box>
                        )}
                    </Stack>
                </MainCard>
            </Stack>
        </>
    );
};

export default ProductPage;
